import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import TranslatedText from "@/components/TranslatedText";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { siteConfig } = useSiteConfig();

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/about", label: "About Us" },
    { href: "/services", label: "Buggy Services" },
    { href: "/blog", label: "Blog" },
    { href: "/contact", label: "Contact" },
  ];

  const isActiveLink = (href: string) => {
    if (href === "/" && location.pathname === "/") return true;
    if (href !== "/" && location.pathname.startsWith(href)) return true;
    return false;
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur-xl supports-[backdrop-filter]:bg-background/85">
      <div className="container flex h-[72px] items-center justify-between px-4">
        <Link to="/" className="flex items-center space-x-2">
          <img 
            src={siteConfig?.logo_header_url || "https://carpeta.tecnosync.com/uploads/68a62ded09aec_1755721197.png"} 
            alt="Ride Punta Cana" 
            width={192}
            height={48}
            decoding="async"
            className="h-11 w-auto"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden items-center gap-6 md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={cn(
                "text-sm font-semibold uppercase transition-colors hover:text-primary",
                isActiveLink(link.href)
                  ? "text-primary"
                  : "text-muted-foreground"
              )}
            >
              <TranslatedText>{link.label}</TranslatedText>
            </Link>
          ))}
          <Button size="sm" asChild><Link to="/services"><TranslatedText>Book Now</TranslatedText></Link></Button>
          <LanguageSwitcher />
        </nav>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-2 md:hidden">
          <LanguageSwitcher />
          <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
          {isMenuOpen ? (
            <X className="h-6 w-6 text-primary" />
          ) : (
            <Menu className="h-6 w-6 text-primary" />
          )}
          </Button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="absolute top-16 left-0 right-0 bg-background border-b shadow-lg md:hidden">
            <nav className="flex flex-col space-y-4 p-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-primary",
                    isActiveLink(link.href)
                      ? "text-primary"
                      : "text-muted-foreground"
                  )}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <TranslatedText>{link.label}</TranslatedText>
                </Link>
              ))}
          <Button size="sm" className="w-fit" asChild><Link to="/services" onClick={() => setIsMenuOpen(false)}><TranslatedText>Book Now</TranslatedText></Link></Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navigation;
